import { useAuth0 } from "@auth0/auth0-react";
import { Dropdown, Table, Tooltip } from "antd";
import { useSnackbar } from "notistack";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { ExternalLink, Mail, UserCheck, Copy } from "lucide-react";
import { ReactSVG } from "react-svg";
import AddUserModal from "../../modals/AddUserModal";
import EditUserModal from "../../modals/EditUserModal";
import UserResetPasswordModal from "../../modals/UserResetPasswordModal";
import UserBlockModal from "../../modals/UserBlockModal";
import UserUnblockModal from "../../modals/UserUnblockModal";
import UserEmailsModal from "../../modals/UserEmailsModal";
import Search from "../../shared/Search";
import { deleteUser, getUsers, exportUsers, toggleUserResellerRole } from "../../utils/users";
import { useContextState } from "../../context/ContextProvider";

const AuthMethodIcon = ({ authMethod }) => {
  if (!authMethod) return null;

  const { provider, providerName } = authMethod;

  if (provider === "google-oauth2") {
    return (
      <Tooltip title={`Signed in with ${providerName}`}>
        <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
      </Tooltip>
    );
  }

  if (provider === "Xero-BackupandRestore" || provider === "Xero-Doconvert") {
    return (
      <Tooltip title={`Signed in with ${providerName}`}>
        <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24">
          <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0z" fill="#13B5EA"/>
          <path d="M6.5 8l3.25 4-3.25 4h2.2l2.15-2.8L13 16h2.2l-3.25-4 3.25-4h-2.2l-2.15 2.8L8.7 8H6.5z" fill="#fff"/>
        </svg>
      </Tooltip>
    );
  }

  // Email / password
  return (
    <Tooltip title={`Signed in with ${providerName}`}>
      <svg className="w-4 h-4 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
      </svg>
    </Tooltip>
  );
};

const CountFilterDropdown = ({ selectedKeys, setSelectedKeys, confirm, clearFilters }) => {
  const options = [
    { label: "Equal to 0", value: "zero" },
    { label: "More than 0", value: "more" },
  ];

  const handleSelect = (value) => {
    const newKeys = selectedKeys.includes(value)
      ? selectedKeys.filter((k) => k !== value)
      : [...selectedKeys, value];
    setSelectedKeys(newKeys);
    confirm({ closeDropdown: false });
  };

  const handleReset = () => {
    clearFilters();
    confirm({ closeDropdown: true });
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden w-44">
      <div className="px-3 pt-3 pb-1">
        {options.map((opt) => (
          <div
            key={opt.value}
            onClick={() => handleSelect(opt.value)}
            className={`flex items-center gap-2 px-2 py-2 rounded-lg cursor-pointer text-sm transition-colors ${
              selectedKeys.includes(opt.value)
                ? "bg-blue-50 text-blue-600 font-medium"
                : "text-gray-700 hover:bg-gray-50"
            }`}
          >
            <span
              className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                selectedKeys.includes(opt.value)
                  ? "bg-blue-600 border-blue-600"
                  : "border-gray-300"
              }`}
            >
              {selectedKeys.includes(opt.value) && (
                <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                  <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </span>
            {opt.label}
          </div>
        ))}
      </div>
      <div className="border-t border-gray-100 px-3 py-2">
        <button
          onClick={handleReset}
          className="text-xs text-blue-500 hover:text-blue-700 font-medium transition-colors disabled:text-gray-300"
          disabled={selectedKeys.length === 0}
        >
          Reset
        </button>
      </div>
    </div>
  );
};

const StatusFilterDropdown = ({ selectedKeys, setSelectedKeys, confirm, clearFilters }) => {
  const options = [
    { label: "Active", value: false, color: "text-green-600 bg-green-50" },
    { label: "Blocked", value: true, color: "text-red-600 bg-red-50" },
  ];

  const handleSelect = (value) => {
    const newKeys = selectedKeys.includes(value)
      ? selectedKeys.filter((k) => k !== value)
      : [...selectedKeys, value];
    setSelectedKeys(newKeys);
    confirm({ closeDropdown: false });
  };

  const handleReset = () => {
    clearFilters();
    confirm({ closeDropdown: true });
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden w-40">
      <div className="px-3 pt-3 pb-1">
        {options.map((opt) => (
          <div
            key={String(opt.value)}
            onClick={() => handleSelect(opt.value)}
            className={`flex items-center gap-2 px-2 py-2 rounded-lg cursor-pointer text-sm transition-colors ${
              selectedKeys.includes(opt.value)
                ? "bg-blue-50 text-blue-600 font-medium"
                : "text-gray-700 hover:bg-gray-50"
            }`}
          >
            <span
              className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                selectedKeys.includes(opt.value)
                  ? "bg-blue-600 border-blue-600"
                  : "border-gray-300"
              }`}
            >
              {selectedKeys.includes(opt.value) && (
                <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                  <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </span>
            <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${opt.color}`}>
              {opt.label}
            </span>
          </div>
        ))}
      </div>
      <div className="border-t border-gray-100 px-3 py-2">
        <button
          onClick={handleReset}
          className="text-xs text-blue-500 hover:text-blue-700 font-medium transition-colors disabled:text-gray-300"
          disabled={selectedKeys.length === 0}
        >
          Reset
        </button>
      </div>
    </div>
  );
};

const Users = () => {
  const { isLoading, getIdTokenClaims } = useAuth0();
  const { enqueueSnackbar } = useSnackbar();
  const { role } = useContextState();
  const [users, setUsers] = useState([]);
  const [showUserResetPasswordModal, setShowUserResetPasswordModal] =
    useState();
  const [userResetPasswordSelected, setUserResetPasswordSelected] = useState();

  const [showUserBlockModal, setShowUserBlockModal] = useState(false);
  const [showUserUnblockModal, setShowUserUnblockModal] = useState(false);
  const [userBlockSelected, setUserBlockSelected] = useState();

  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [userEditSelected, setUserEditSelected] = useState(null);

  const [showUserEmailsModal, setShowUserEmailsModal] = useState(false);
  const [userEmailsSelected, setUserEmailsSelected] = useState(null);

  const [token, setToken] = useState();
  const [fetching, setFetching] = useState(false);
  const [query, setQuery] = useState("");
  const [exporting, setExporting] = useState(false);

  const [addUserModalOpen, setAddUserModalOpen] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      fetchUsers();
    }
  }, [isLoading, query]);

  const fetchUsers = async () => {
    try {
      setFetching(true);
      setUsers([]);

      const token = await getIdTokenClaims();

      setToken(token.__raw);

      getUsers(token.__raw, query)
        .then((res) => {
          if (res.data.success === false) {
            enqueueSnackbar(res.data.message, {
              variant: "error",
            });
          } else {
            setUsers(res.data.users);
          }
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          setFetching(false);
        });
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteClicked = (id) => {
    if (window.confirm("Are you sure you want to delete the user?")) {
      setFetching(true);

      deleteUser(token, id)
        .then((res) => {
          enqueueSnackbar("Successfully deleted the user!", {
            variant: "success",
          });

          fetchUsers();
        })
        .catch((err) => {
          enqueueSnackbar("Something went wrong. Please contact support.", {
            variant: "error",
          });
          console.error(err);
        });
    }
  };

  const handleToggleReseller = async (record) => {
    const isCurrentlyReseller = record.role === "reseller";
    const actionText = isCurrentlyReseller
      ? `revert ${record.name || record.email} to a standard User`
      : `mark ${record.name || record.email} as a Reseller`;

    if (!window.confirm(`Are you sure you want to ${actionText}?`)) {
      return;
    }

    try {
      setFetching(true);
      const res = await toggleUserResellerRole(token, record.id);
      if (res.data.success) {
        enqueueSnackbar(res.data.message, { variant: "success" });
        fetchUsers();
      } else {
        enqueueSnackbar(res.data.message || "Failed to update reseller status.", {
          variant: "error",
        });
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("An error occurred while updating reseller status.", {
        variant: "error",
      });
    } finally {
      setFetching(false);
    }
  };

  const handleCopyInviteLink = (referralCode) => {
    if (!referralCode) {
      enqueueSnackbar("No referral code found for this reseller yet.", {
        variant: "warning",
      });
      return;
    }
    const inviteUrl = `${window.location.origin}/reseller-invite/${referralCode}`;
    navigator.clipboard.writeText(inviteUrl);
    enqueueSnackbar("Reseller invite link copied to clipboard!", {
      variant: "success",
    });
  };

  const handleSearchChange = useCallback(
    debounce((text) => {
      setQuery(text);
    }, 500),
    []
  );

  const handleAddUserModalClose = () => {
    setAddUserModalOpen(false);
  };

  const handleBlockSuccess = () => {
    fetchUsers();
  };

  const handleUnblockSuccess = () => {
    fetchUsers();
  };

  const handleExportUsers = async () => {
    try {
      setExporting(true);

      const response = await exportUsers(token);

      // Create a blob from the response data
      const blob = new Blob([response.data], { type: 'text/csv' });

      // Create a download link and trigger it
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      enqueueSnackbar('Users exported successfully!', {
        variant: "success",
      });
    } catch (error) {
      console.error('Export error:', error);
      enqueueSnackbar('Failed to export users. Please try again.', {
        variant: "error",
      });
    } finally {
      setExporting(false);
    }
  };

  const handleNavigateToOrgs = (userEmail) => {
    if (!userEmail) return;
    const searchParams = new URLSearchParams();
    searchParams.set("searchType", "owner");
    searchParams.set("searchQuery", userEmail.trim());
    window.open(`/organizations?${searchParams.toString()}`, "_blank");
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "30%",
      render: (_, record) => {
        const { name, email, auth_method } = record;
        const displayName = name || email || "—";
        return (
          <div className={"flex gap-2 items-center justify-between group/user text-gray-800"}>
            <div className="flex gap-2 items-center min-w-0">
              {role === "superadmin" && <AuthMethodIcon authMethod={auth_method} />}
              <button
                type="button"
                onClick={() => handleNavigateToOrgs(email)}
                className="font-medium text-gray-900 hover:text-blue-600 hover:underline cursor-pointer text-left transition-colors truncate"
                title={`Quick link to Organizations for ${displayName}`}
              >
                {displayName}
              </button>
            </div>
            {email && (
              <Tooltip title={`View organizations for ${displayName}`}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNavigateToOrgs(email);
                  }}
                  className="opacity-0 group-hover/user:opacity-100 transition-opacity p-1 text-blue-600 hover:bg-blue-50 rounded cursor-pointer shrink-0"
                  aria-label="View organizations"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </button>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email, record) => (
        <div>
          <div>
            <button
              type="button"
              onClick={() => handleNavigateToOrgs(email)}
              className="text-gray-700 hover:text-blue-600 hover:underline cursor-pointer text-left transition-colors font-normal"
              title={`View organizations for ${email}`}
            >
              {email}
            </button>
          </div>
          {record?.alternate_email ? (
            <div
              className="text-xs text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded mt-1 inline-block max-w-xs truncate font-normal"
              title={`Alternate Email (CC): ${record.alternate_email}`}
            >
              CC: {record.alternate_email}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      title: "Company Phone",
      dataIndex: "company_phone",
      key: "company_phone",
      render: (company_phone) => {
        const value =
          company_phone === null || company_phone === undefined || company_phone === ""
            ? null
            : String(company_phone);
        return <span className="text-gray-500">{value || "—"}</span>;
      },
    },
    {
      title: "Created At",
      dataIndex: "created",
      key: "date",
      sorter: (a, b) =>
        String(a.created || "\uffff").localeCompare(String(b.created || "\uffff")),
      defaultSortOrder: "descend",
    },
    {
      title: "Last Login",
      dataIndex: "last_login",
      key: "last_login",
      sorter: (a, b) => {
        const timeA = a.last_login ? new Date(a.last_login).getTime() : 0;
        const timeB = b.last_login ? new Date(b.last_login).getTime() : 0;
        return timeA - timeB;
      },
      render: (last_login) => {
        if (!last_login) {
          return <span className="text-gray-400">—</span>;
        }
        return <span className="text-gray-600">{last_login}</span>;
      },
    },
    {
      title: "Connected",
      dataIndex: "connected",
      key: "connected",
      align: "center",
      filterDropdown: ({ selectedKeys, setSelectedKeys, confirm, clearFilters }) => (
        <CountFilterDropdown
          selectedKeys={selectedKeys}
          setSelectedKeys={setSelectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
        />
      ),
      onFilter: (value, record) => {
        const count = Number(record.connected) || 0;
        return value === "zero" ? count === 0 : count > 0;
      },
      render: (connected, record) => (
        <button
          type="button"
          onClick={() => handleNavigateToOrgs(record.email)}
          disabled={!record.email}
          className="text-green-600 bg-green-50 hover:bg-green-100 px-2 py-1 rounded-lg text-sm font-medium transition-colors cursor-pointer"
          title={`View organizations for ${record.email}`}
        >
          {connected || 0}
        </button>
      ),
    },
    {
      title: "Disconnected",
      dataIndex: "disconnected",
      key: "disconnected",
      align: "center",
      filterDropdown: ({ selectedKeys, setSelectedKeys, confirm, clearFilters }) => (
        <CountFilterDropdown
          selectedKeys={selectedKeys}
          setSelectedKeys={setSelectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
        />
      ),
      onFilter: (value, record) => {
        const count = Number(record.disconnected) || 0;
        return value === "zero" ? count === 0 : count > 0;
      },
      render: (disconnected, record) => (
        <button
          type="button"
          onClick={() => handleNavigateToOrgs(record.email)}
          disabled={!record.email}
          className="text-red-600 bg-red-50 hover:bg-red-100 px-2 py-1 rounded-lg text-sm font-medium transition-colors cursor-pointer"
          title={`View organizations for ${record.email}`}
        >
          {disconnected || 0}
        </button>
      ),
    },
    {
      title: "Not subscribed",
      dataIndex: "not_subscribed",
      key: "notSubscribed",
      align: "center",
      filterDropdown: ({ selectedKeys, setSelectedKeys, confirm, clearFilters }) => (
        <CountFilterDropdown
          selectedKeys={selectedKeys}
          setSelectedKeys={setSelectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
        />
      ),
      onFilter: (value, record) => {
        const count = Number(record.not_subscribed) || 0;
        return value === "zero" ? count === 0 : count > 0;
      },
      render: (notSubscribed, record) => (
        <button
          type="button"
          onClick={() => handleNavigateToOrgs(record.email)}
          disabled={!record.email}
          className="text-gray-600 bg-gray-50 hover:bg-gray-100 px-2 py-1 rounded-lg text-sm font-medium transition-colors cursor-pointer"
          title={`View organizations for ${record.email}`}
        >
          {notSubscribed || 0}
        </button>
      ),
    },
    {
      title: "Status",
      dataIndex: "is_blocked",
      key: "status",
      width: "15%",
      filterDropdown: ({ selectedKeys, setSelectedKeys, confirm, clearFilters }) => (
        <StatusFilterDropdown
          selectedKeys={selectedKeys}
          setSelectedKeys={setSelectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
        />
      ),
      onFilter: (value, record) => record.is_blocked === value,
      render: (_, { is_blocked }) => {
        return (
          <div className={"text-gray-500"}>
            {is_blocked ? (
              <span className={"text-red-600 bg-red-50 px-2 py-1 rounded-lg text-sm font-medium"}>
                Blocked
              </span>
            ) : (
              <span className={"text-green-600 bg-green-50 px-2 py-1 rounded-lg text-sm font-medium"}>
                Active
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: "30%",
      render: (_, record) => {
        const { id, role: userRole, is_blocked } = record;
        const isSuperAdmin = role === "superadmin";
        const isTargetSuperAdmin = userRole === "superadmin";

        return (
          <div className={"text-gray-500 flex justify-between items-center"}>
            <div
              className={`flex w-max rounded-lg p-1 px-2 gap-2 items-center ${roleHighlight[userRole]}`}
            >
              {userRole}
            </div>

            <Dropdown
              placement="bottomRight"
              trigger={["click"]}
              dropdownRender={(menu) => (
                <div
                  className={
                    "bg-white w-48 p-2 border border-gray-100 rounded-lg shadow-lg"
                  }
                >
                  <div
                    className={
                      "font-medium flex items-center gap-2 text-blue-600 rounded-lg hover:bg-blue-50 p-2 text-sm cursor-pointer"
                    }
                    onClick={() => {
                      handleNavigateToOrgs(record.email);
                    }}
                  >
                    <ReactSVG src={"/assets/icons/organization.svg"} className="w-4 h-4 flex-shrink-0" />
                    View Organizations
                  </div>

                  <div
                    className={
                      "font-medium flex items-center gap-2 text-indigo-600 rounded-lg hover:bg-indigo-50 p-2 text-sm cursor-pointer"
                    }
                    onClick={() => {
                      setUserEmailsSelected(record);
                      setShowUserEmailsModal(true);
                    }}
                  >
                    <Mail className="w-4 h-4 flex-shrink-0" />
                    Emails
                  </div>

                  {isSuperAdmin && (
                    <div
                      className={
                        "font-medium flex items-center gap-2 rounded-lg hover:bg-gray-50 p-2 text-sm cursor-pointer"
                      }
                      onClick={() => {
                        setUserEditSelected(record);
                        setShowEditUserModal(true);
                      }}
                    >
                      <ReactSVG src={"/assets/icons/edit.svg"} />
                      Edit
                    </div>
                  )}

                  <div
                    className={
                      "font-medium flex items-center gap-2 rounded-lg hover:bg-gray-50 p-2 text-sm cursor-pointer"
                    }
                    onClick={() => {
                      setShowUserResetPasswordModal(true);
                      setUserResetPasswordSelected(id);
                    }}
                  >
                    <ReactSVG src={"/assets/icons/edit.svg"} />
                    Reset Password
                  </div>

                  {isSuperAdmin && !isTargetSuperAdmin && (
                    <div
                      className={
                        "font-medium flex items-center gap-2 text-purple-700 rounded-lg hover:bg-purple-50 p-2 text-sm cursor-pointer"
                      }
                      onClick={() => handleToggleReseller(record)}
                    >
                      <UserCheck className="w-4 h-4 flex-shrink-0" />
                      {record.role === "reseller" ? "Unmark as Reseller" : "Mark as Reseller"}
                    </div>
                  )}

                  {record.role === "reseller" && record.referral_code && (
                    <div
                      className={
                        "font-medium flex items-center gap-2 text-blue-600 rounded-lg hover:bg-blue-50 p-2 text-sm cursor-pointer"
                      }
                      onClick={() => handleCopyInviteLink(record.referral_code)}
                    >
                      <Copy className="w-4 h-4 flex-shrink-0" />
                      Copy Invite Link
                    </div>
                  )}

                  {isSuperAdmin && !isTargetSuperAdmin && !is_blocked && (
                    <div
                      className={
                        "font-medium flex items-center gap-2 text-orange-600 rounded-lg hover:bg-gray-50 p-2 text-sm cursor-pointer"
                      }
                      onClick={() => {
                        setShowUserBlockModal(true);
                        setUserBlockSelected(id);
                      }}
                    >
                      <ReactSVG src={"/assets/icons/delete.svg"} />
                      Block
                    </div>
                  )}

                  {isSuperAdmin && is_blocked && (
                    <div
                      className={
                        "font-medium flex items-center gap-2 text-green-600 rounded-lg hover:bg-gray-50 p-2 text-sm cursor-pointer"
                      }
                      onClick={() => {
                        setShowUserUnblockModal(true);
                        setUserBlockSelected(id);
                      }}
                    >
                      <ReactSVG src={"/assets/icons/edit.svg"} />
                      Unblock
                    </div>
                  )}

                  <div
                    className={
                      "font-medium flex items-center gap-2  text-rose-500 rounded-lg hover:bg-gray-50 p-2 text-sm cursor-pointer"
                    }
                    onClick={() => {
                      handleDeleteClicked(id);
                    }}
                  >
                    <ReactSVG src={"/assets/icons/delete.svg"} />
                    Delete
                  </div>
                </div>
              )}
            >
              <ReactSVG
                className={"p-1 cursor-pointer rounded-lg hover:bg-white"}
                src={"/assets/icons/dot-menu.svg"}
              />
            </Dropdown>
          </div>
        );
      },
    },
  ];

  const roleHighlight = {
    Admin: "text-slate-600 bg-slate-50",
    Manager: "text-green-600 bg-green-50",
    "Super Admin": "text-orange-600 bg-orange-50",
    superadmin: "text-orange-600 bg-orange-50",
    reseller: "text-purple-700 bg-purple-50 font-medium border border-purple-200",
    Reseller: "text-purple-700 bg-purple-50 font-medium border border-purple-200",
    user: "text-slate-600 bg-slate-50",
  };

  const mobileRender = users.map((item, index) => {
    const isSuperAdmin = role === "superadmin";
    const isTargetSuperAdmin = item.role === "superadmin";

    return (
      <div
        key={index}
        className={
          "flex justify-between border rounded-lg border-gray-300 text-sm py-3 mt-3 px-4 text-gray-500"
        }
      >
        {/*<div>*/}
        {/*    image goes here*/}
        {/*</div>*/}
        <div>
          <p className={"mb-2 font-medium text-gray-600"}>
            <button
              type="button"
              onClick={() => handleNavigateToOrgs(item.email)}
              className="text-left font-medium text-gray-800 hover:text-blue-600 hover:underline cursor-pointer"
              title={`View organizations for ${item.name || item.email}`}
            >
              {item.name}
            </button>
          </p>
          <p className={"mb-2"}>
            <button
              type="button"
              onClick={() => handleNavigateToOrgs(item.email)}
              className="text-left text-gray-500 hover:text-blue-600 hover:underline cursor-pointer"
              title={`View organizations for ${item.email}`}
            >
              {item.email}
            </button>
          </p>
          {(item.company_phone === 0 || item.company_phone) && (
            <p className={"mb-2"}>{String(item.company_phone)}</p>
          )}
          {item.last_login && (
            <p className={"mb-2 text-xs text-gray-400"}>Last Login: {item.last_login}</p>
          )}
          <div className={"mb-2"}>
            {item.is_blocked ? (
              <span className={"text-red-600 bg-red-50 px-2 py-1 rounded-lg text-xs font-medium"}>
                Blocked
              </span>
            ) : (
              <span className={"text-green-600 bg-green-50 px-2 py-1 rounded-lg text-xs font-medium"}>
                Active
              </span>
            )}
          </div>
          <div
            className={`flex w-max rounded-lg p-1 px-2 gap-2 items-center ${roleHighlight[item.role]
              }`}
          >
            {item.role}
          </div>
        </div>
        <div className={"flex items-end flex-col justify-between"}>
          <div>{item.date}</div>
          <Dropdown
            placement="bottomRight"
            trigger={["click"]}
            dropdownRender={(menu) => (
              <div
                className={
                  "bg-white w-48 p-2 border border-gray-100 rounded-lg shadow-lg"
                }
              >
                <div
                  className={
                    "font-medium flex items-center gap-2 text-blue-600 rounded-lg hover:bg-blue-50 p-2 text-sm cursor-pointer"
                  }
                  onClick={() => {
                    handleNavigateToOrgs(item.email);
                  }}
                >
                  <ReactSVG src={"/assets/icons/organization.svg"} className="w-4 h-4 flex-shrink-0" />
                  View Organizations
                </div>

                <div
                  className={
                    "font-medium flex items-center gap-2 text-indigo-600 rounded-lg hover:bg-indigo-50 p-2 text-sm cursor-pointer"
                  }
                  onClick={() => {
                    setUserEmailsSelected(item);
                    setShowUserEmailsModal(true);
                  }}
                >
                  <Mail className="w-4 h-4 flex-shrink-0" />
                  Emails
                </div>

                {isSuperAdmin && (
                  <div
                    className={
                      "font-medium flex items-center gap-2 rounded-lg hover:bg-gray-50 p-2 text-sm cursor-pointer"
                    }
                    onClick={() => {
                      setUserEditSelected(item);
                      setShowEditUserModal(true);
                    }}
                  >
                    <ReactSVG src={"/assets/icons/edit.svg"} />
                    Edit
                  </div>
                )}

                <div
                  className={
                    "font-medium flex items-center gap-2 rounded-lg hover:bg-gray-50 p-2 text-sm cursor-pointer"
                  }
                  onClick={() => {
                    setShowUserResetPasswordModal(true);
                    setUserResetPasswordSelected(item.id);
                  }}
                >
                  <ReactSVG src={"/assets/icons/edit.svg"} />
                  Reset Password
                </div>

                {isSuperAdmin && !isTargetSuperAdmin && (
                  <div
                    className={
                      "font-medium flex items-center gap-2 text-purple-700 rounded-lg hover:bg-purple-50 p-2 text-sm cursor-pointer"
                    }
                    onClick={() => handleToggleReseller(item)}
                  >
                    <UserCheck className="w-4 h-4 flex-shrink-0" />
                    {item.role === "reseller" ? "Unmark as Reseller" : "Mark as Reseller"}
                  </div>
                )}

                {item.role === "reseller" && item.referral_code && (
                  <div
                    className={
                      "font-medium flex items-center gap-2 text-blue-600 rounded-lg hover:bg-blue-50 p-2 text-sm cursor-pointer"
                    }
                    onClick={() => handleCopyInviteLink(item.referral_code)}
                  >
                    <Copy className="w-4 h-4 flex-shrink-0" />
                    Copy Invite Link
                  </div>
                )}

                {isSuperAdmin && !isTargetSuperAdmin && !item.is_blocked && (
                  <div
                    className={
                      "font-medium flex items-center gap-2 text-orange-600 rounded-lg hover:bg-gray-50 p-2 text-sm cursor-pointer"
                    }
                    onClick={() => {
                      setShowUserBlockModal(true);
                      setUserBlockSelected(item.id);
                    }}
                  >
                    <ReactSVG src={"/assets/icons/delete.svg"} />
                    Block
                  </div>
                )}

                {isSuperAdmin && item.is_blocked && (
                  <div
                    className={
                      "font-medium flex items-center gap-2 text-green-600 rounded-lg hover:bg-gray-50 p-2 text-sm cursor-pointer"
                    }
                    onClick={() => {
                      setShowUserUnblockModal(true);
                      setUserBlockSelected(item.id);
                    }}
                  >
                    <ReactSVG src={"/assets/icons/edit.svg"} />
                    Unblock
                  </div>
                )}

                <div
                  className={
                    "font-medium flex items-center gap-2  text-rose-500 rounded-lg hover:bg-gray-50 p-2 text-sm cursor-pointer"
                  }
                  onClick={() => {
                    handleDeleteClicked(item.id);
                  }}
                >
                  <ReactSVG src={"/assets/icons/delete.svg"} />
                  Delete
                </div>
              </div>
            )}
          >
            <ReactSVG
              className={"p-1 cursor-pointer rounded-lg hover:bg-white"}
              src={"/assets/icons/dot-menu.svg"}
            />
          </Dropdown>
        </div>
      </div>
    );
  });

  return (
    <div className={"px-8"}>
      <AddUserModal
        modalOpen={addUserModalOpen}
        onClose={handleAddUserModalClose}
      />

      <EditUserModal
        modalOpen={showEditUserModal}
        user={userEditSelected}
        onClose={() => {
          setShowEditUserModal(false);
          setUserEditSelected(null);
        }}
        onSuccess={() => {
          fetchUsers();
        }}
      />

      <UserResetPasswordModal
        showModal={showUserResetPasswordModal}
        id={userResetPasswordSelected}
        onClose={() => {
          setShowUserResetPasswordModal(false);
        }}
      />

      <UserBlockModal
        showModal={showUserBlockModal}
        id={userBlockSelected}
        onClose={() => {
          setShowUserBlockModal(false);
        }}
        onSuccess={handleBlockSuccess}
      />

      <UserUnblockModal
        showModal={showUserUnblockModal}
        id={userBlockSelected}
        onClose={() => {
          setShowUserUnblockModal(false);
        }}
        onSuccess={handleUnblockSuccess}
      />

      <UserEmailsModal
        open={showUserEmailsModal}
        onClose={() => {
          setShowUserEmailsModal(false);
          setUserEmailsSelected(null);
        }}
        user={userEmailsSelected}
      />

      <div className={"flex items-center justify-between"}>
        <Search
          placeholder={"Search Users..."}
          handleChange={handleSearchChange}
        />

        {role === "superadmin" && (
          <button
            onClick={handleExportUsers}
            disabled={exporting}
            className={
              "bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg text-sm transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            }
          >
            {exporting ? "Exporting..." : "Export Users"}
          </button>
        )}
      </div>
      <div className={"mt-6 lg:block pb-3 mb-10 hidden"}>
        <Table
          rowKey="id"
          dataSource={users}
          columns={columns}
          loading={fetching}
          onRow={(record) => ({
            onClick: (e) => {
              if (
                e.target.closest("button") ||
                e.target.closest(".ant-dropdown-trigger") ||
                e.target.closest(".ant-dropdown") ||
                e.target.closest("a") ||
                e.target.closest("input")
              ) {
                return;
              }
              const selection = window.getSelection();
              if (selection && selection.toString().length > 0) {
                return;
              }
              handleNavigateToOrgs(record.email);
            },
            className: "cursor-pointer hover:bg-blue-50/30 transition-colors",
          })}
          pagination={{
            showSizeChanger: true, // Enables the dropdown for selecting rows per page
            pageSizeOptions: ["10", "20", "50", "100"], // Defines options for rows per page
            defaultPageSize: 10, // Default number of rows per page
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
        />
      </div>
      <div className={"lg:hidden block"}>{mobileRender}</div>
    </div>
  );
};

export default Users;
